import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Upload, X, Pencil, Plus, Loader2, Languages } from "lucide-react";
import { HotelManualInfo } from "@/hooks/useHotelManualInfo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface HotelManualInfoEditorProps {
  hotelId: string | number;
  hotelName: string;
  existingInfo: HotelManualInfo | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function HotelManualInfoEditor({
  hotelId,
  hotelName,
  existingInfo,
  open,
  onOpenChange,
  onSaved,
}: HotelManualInfoEditorProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  // Form state - multilingual
  const [reviewTextEs, setReviewTextEs] = useState("");
  const [reviewTextPt, setReviewTextPt] = useState("");
  const [reviewTextEn, setReviewTextEn] = useState("");
  const [isPetFriendly, setIsPetFriendly] = useState<boolean | null>(null);
  const [photos, setPhotos] = useState<string[]>([]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setReviewTextEs(existingInfo?.review_text_es || "");
      setReviewTextPt(existingInfo?.review_text_pt || "");
      setReviewTextEn(existingInfo?.review_text_en || "");
      setIsPetFriendly(existingInfo?.is_pet_friendly ?? null);
      setPhotos(existingInfo?.photos || []);
    }
  }, [open, existingInfo]);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const newPhotos: string[] = [];

    for (const file of Array.from(files)) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${hotelId}-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('hotel-photos')
        .upload(fileName, file);

      if (uploadError) {
        toast({ title: `Error al subir ${file.name}`, variant: "destructive" });
        continue;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('hotel-photos')
        .getPublicUrl(fileName);

      newPhotos.push(publicUrl);
    }

    setPhotos([...photos, ...newPhotos]);
    setUploading(false);
    e.target.value = '';
  };

  const removePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleTranslate = async (sourceLang: 'es' | 'pt' | 'en') => {
    const sourceTexts = { es: reviewTextEs, pt: reviewTextPt, en: reviewTextEn };
    const sourceText = sourceTexts[sourceLang];
    
    if (!sourceText.trim()) {
      toast({ 
        title: "No hay texto para traducir", 
        description: `Por favor, escriba el texto en ${sourceLang === 'es' ? 'español' : sourceLang === 'pt' ? 'portugués' : 'inglés'} primero.`,
        variant: "destructive" 
      });
      return;
    }

    // Determine which languages need translation
    const targetLanguages: ('es' | 'pt' | 'en')[] = [];
    if (sourceLang !== 'es' && !reviewTextEs.trim()) targetLanguages.push('es');
    if (sourceLang !== 'pt' && !reviewTextPt.trim()) targetLanguages.push('pt');
    if (sourceLang !== 'en' && !reviewTextEn.trim()) targetLanguages.push('en');

    if (targetLanguages.length === 0) {
      toast({ 
        title: "Todos los idiomas ya tienen texto", 
        description: "No hay idiomas vacíos para traducir.",
      });
      return;
    }

    setTranslating(true);

    try {
      const { data, error } = await supabase.functions.invoke('translate-review', {
        body: {
          sourceText: sourceText.trim(),
          sourceLanguage: sourceLang,
          targetLanguages,
        },
      });

      if (error) throw error;

      if (data?.translations) {
        if (data.translations.es && targetLanguages.includes('es')) {
          setReviewTextEs(data.translations.es);
        }
        if (data.translations.pt && targetLanguages.includes('pt')) {
          setReviewTextPt(data.translations.pt);
        }
        if (data.translations.en && targetLanguages.includes('en')) {
          setReviewTextEn(data.translations.en);
        }
        
        toast({ 
          title: "Traducción completada", 
          description: `Texto traducido a ${targetLanguages.length} idioma(s).` 
        });
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast({ 
        title: "Error al traducir", 
        description: error instanceof Error ? error.message : "Error desconocido",
        variant: "destructive" 
      });
    } finally {
      setTranslating(false);
    }
  };

  const getAvailableSourceLanguages = () => {
    const sources: { lang: 'es' | 'pt' | 'en'; label: string; hasText: boolean }[] = [
      { lang: 'es', label: '🇪🇸 Español', hasText: !!reviewTextEs.trim() },
      { lang: 'pt', label: '🇧🇷 Português', hasText: !!reviewTextPt.trim() },
      { lang: 'en', label: '🇬🇧 English', hasText: !!reviewTextEn.trim() },
    ];
    return sources.filter(s => s.hasText);
  };

  const handleSave = async () => {
    setSaving(true);

    const { data: { session } } = await supabase.auth.getSession();
    
    const hotelData = {
      hotel_id: hotelId.toString(),
      review_text_es: reviewTextEs.trim() || null,
      review_text_pt: reviewTextPt.trim() || null,
      review_text_en: reviewTextEn.trim() || null,
      is_pet_friendly: isPetFriendly,
      photos: photos,
      updated_by: session?.user?.id || null,
    };

    let error;

    if (existingInfo) {
      ({ error } = await supabase
        .from("hotel_manual_info")
        .update(hotelData)
        .eq("id", existingInfo.id));
    } else {
      ({ error } = await supabase
        .from("hotel_manual_info")
        .insert({
          ...hotelData,
          created_by: session?.user?.id || null,
        }));
    }

    setSaving(false);

    if (error) {
      toast({ title: "Error al guardar", description: error.message, variant: "destructive" });
      return;
    }

    toast({ title: existingInfo ? "Información actualizada" : "Información agregada" });
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {existingInfo ? <Pencil className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
            {existingInfo ? "Editar Información Manual" : "Agregar Información Manual"}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">{hotelName}</p>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Multilingual Review Text */}
          <div className="space-y-2">
            <Label>Texto de Evaluación</Label>
            <Tabs defaultValue="es" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="es" className="gap-1">
                  🇪🇸 Español
                </TabsTrigger>
                <TabsTrigger value="pt" className="gap-1">
                  🇧🇷 Português
                </TabsTrigger>
                <TabsTrigger value="en" className="gap-1">
                  🇬🇧 English
                </TabsTrigger>
              </TabsList>
              <TabsContent value="es" className="mt-3">
                <Textarea
                  value={reviewTextEs}
                  onChange={(e) => setReviewTextEs(e.target.value)}
                  placeholder="Escriba aquí su evaluación del hotel en español..."
                  rows={5}
                />
              </TabsContent>
              <TabsContent value="pt" className="mt-3">
                <Textarea
                  value={reviewTextPt}
                  onChange={(e) => setReviewTextPt(e.target.value)}
                  placeholder="Escreva aqui sua avaliação do hotel em português..."
                  rows={5}
                />
              </TabsContent>
              <TabsContent value="en" className="mt-3">
                <Textarea
                  value={reviewTextEn}
                  onChange={(e) => setReviewTextEn(e.target.value)}
                  placeholder="Write your hotel review here in English..."
                  rows={5}
                />
              </TabsContent>
            </Tabs>
            
            {/* Auto-translate button */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Escriba el texto en un idioma y use IA para traducir automáticamente a los demás.
              </p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={translating || getAvailableSourceLanguages().length === 0}
                    className="gap-2"
                  >
                    {translating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Traduciendo...
                      </>
                    ) : (
                      <>
                        <Languages className="h-4 w-4" />
                        Traducir con IA
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {getAvailableSourceLanguages().map((source) => (
                    <DropdownMenuItem 
                      key={source.lang}
                      onClick={() => handleTranslate(source.lang)}
                    >
                      Traducir desde {source.label}
                    </DropdownMenuItem>
                  ))}
                  {getAvailableSourceLanguages().length === 0 && (
                    <DropdownMenuItem disabled>
                      Escriba texto en algún idioma primero
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Pet-Friendly</Label>
              <p className="text-xs text-muted-foreground">
                ¿Este hotel acepta mascotas?
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={isPetFriendly === true ? "default" : "outline"}
                size="sm"
                onClick={() => setIsPetFriendly(true)}
              >
                Sí
              </Button>
              <Button
                type="button"
                variant={isPetFriendly === false ? "default" : "outline"}
                size="sm"
                onClick={() => setIsPetFriendly(false)}
              >
                No
              </Button>
              <Button
                type="button"
                variant={isPetFriendly === null ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setIsPetFriendly(null)}
              >
                N/A
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Fotos del Hotel</Label>
            <div className="grid grid-cols-3 gap-3">
              {photos.map((photo, index) => (
                <div key={index} className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                  <img
                    src={photo}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6"
                    onClick={() => removePhoto(index)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <label className="aspect-video border-2 border-dashed border-muted-foreground/25 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploading}
                />
                {uploading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-muted-foreground mb-1" />
                    <span className="text-xs text-muted-foreground">Subir fotos</span>
                  </>
                )}
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                existingInfo ? "Actualizar" : "Guardar"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
